import { translateBackend } from '@assets/i18n/config';
import MugImage from '@assets/images/custom/mug.svg';
import { FeatureFlagTypes } from '@generated/graphql';

import { Link } from '@ovComponents/1-primative/link/link';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';

import { FullScreenDialog } from '@ovComponents/3-pattern/fullScreenDialog/fullScreenDialog';
import { legacyColors } from '@ovComponents/legacyColor';
import { AnalyticsContext } from '@providers/analyticsProvider';
import {
  UserContext,
  useOrganizationFeatureFlag,
} from '@providers/userContextProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { Box, Button, IconButton, Typography, useTheme } from 'hephaestus/web';
import { ChevronLeft, Close as CloseIcon } from 'hephaestus/webIcons';
import { kebabCase } from 'lodash-es';
import {
  type Dispatch,
  type SetStateAction,
  useContext,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { StepIndicator } from './components/stepIndicator';
import { Account } from './subSteps/account/account';
import { AddModelPortfolioDetails } from './subSteps/addModelPortfolioDetails/addModelPortfolioDetails';
import { AddModelPortfolioSecurities } from './subSteps/addModelPortfolioSecurities/addModelPortfolioSecurities';
import { AddModelPortfoliosIPSAndAssetClasses } from './subSteps/addModelPortfoliosIPSAndAssetClasses/addModelPortfoliosIPSAndAssetClasses';
import { Approval } from './subSteps/approval/approval';
import { ArchiveObjects } from './subSteps/archiveObjects/archiveObjects';
import { AssignPortfolioToSubAccount } from './subSteps/assignPortfolioToSubAccount/assignPortfolioToSubAccount';
import { Calculator } from './subSteps/calculator/calculator';
import { CollectSigners } from './subSteps/collectSigners/collectSigners';
import { CreateAccount } from './subSteps/createAccount/createAccount';
import { CreateAccountForGoal } from './subSteps/createAccountForGoal/createAccountForGoal';
import { CreateAccountGroup } from './subSteps/createAccountGroup/createAccountGroup';
import { CreateAssetTemplate } from './subSteps/createAssetTemplate/createAssetTemplate';
import { CreateGoal } from './subSteps/createGoal/createGoal';
import { CreateIntegrationFormPack } from './subSteps/createIntegrationFormPack/createIntegrationFormPack';
import { CreateModelPortfolio } from './subSteps/createModelPortfolio/createModelPortfolio';
import { CreateSubAccountForAccount } from './subSteps/createSubAccountForAccount/createSubAccountForAccount';
import { DepositForm } from './subSteps/depositForm/depositForm';
import { DepositReview } from './subSteps/depositReview/depositReview';
import { DepositType } from './subSteps/depositType/depositType';
import { DetachModelPortfolio } from './subSteps/detachModelPortfolio/detachModelPortfolio';
import { Documents } from './subSteps/documents/documents';
import { EditAccount } from './subSteps/editAccount/editAccount';
import { EditGoal } from './subSteps/editGoal/editGoal';
import { EditHouseholdModelPortfolioIps } from './subSteps/editHouseholdModelPortfolioIps/editHouseholdModelPortfolioIps';
import { EmploymentInformation } from './subSteps/employmentInformation/employmentInformation';
import { FinancialProjection } from './subSteps/financialProjection/financialProjection';
import { GoalRiskQuestion } from './subSteps/goalRiskQuestion/goalRiskQuestion';
import { Goals } from './subSteps/goals/goals';
import { Household } from './subSteps/household/household';
import { IdVerification } from './subSteps/idVerification/idVerification';
import { IncomeAssetsAndDebts } from './subSteps/incomeAssetsAndDebts/incomeAssetsAndDebts';
import { InitializeHouseholdModelPortfolio } from './subSteps/initializeHouseholdModelPortfolio/initializeHouseholdModelPortfolio';
import { InvestmentKnowledge } from './subSteps/investmentKnowledge/investmentKnowledge';
import { ManageFormCompletions } from './subSteps/manageFormCompletions/manageFormCompletions';
import { ManageSigners } from './subSteps/manageSigners/manageSigners';
import { NetWorthAdd } from './subSteps/netWorth/netWorthAdd';
import { NonIndividualInformation } from './subSteps/nonIndividualInformation/nonIndividualInformation';
import { NonIndividualSetup } from './subSteps/nonIndividualSetup/nonIndividualSetup';
import { PersonalInformation } from './subSteps/personalInformation/personalInformation';
import { PersonsOfInterest } from './subSteps/personsOfInterest/personsOfInterest';
import { PrepareFormCompletions } from './subSteps/prepareFormCompletions/prepareFormCompletions';
import { RedirectHandler } from './subSteps/redirectHandler/redirectHandler';
import { RelatedEntities } from './subSteps/relatedEntities/relatedEntities';
import { RelationshipInformation } from './subSteps/relationshipInformation/relationshipInformation';
import { ResidencyInformation } from './subSteps/residencyInformation/residencyInformation';
import { Review } from './subSteps/review/review';
import { ReviewAccount } from './subSteps/reviewAccount/reviewAccount';
import { ReviewAllocations } from './subSteps/reviewAllocations/reviewAllocations';
import { ReviewBankAccountInformation } from './subSteps/reviewBankAccountInformation/reviewBankAccountInformation';
import { ReviewGoal } from './subSteps/reviewGoal/reviewGoal';
import { ReviewIpsSummary } from './subSteps/reviewIpsSummary/reviewIpsSummary';
import { ReviewPortfolioChanges } from './subSteps/reviewPortfolioChanges/reviewPortfolioChanges';
import { RiskProfile } from './subSteps/riskProfile/riskProfile';
import { RiskQuestion } from './subSteps/riskQuestion/riskQuestion';
import { SelectHouseholdParentModelPortfolio } from './subSteps/selectHouseholdParentModelPortfolio/selectHouseholdParentModelPortfolio';
import { SelectModelPortfolioEditType } from './subSteps/selectModelPortfolioEditType/selectModelPortfolioEditType';
import { SendIntegrationFormPack } from './subSteps/sendIntegrationFormPack/sendIntegrationFormPack';
import { SignFormCompletions } from './subSteps/signFormCompletions/signFormCompletions';
import { SimpleForm } from './subSteps/simpleForm/simpleForm';
import { SubAccounts } from './subSteps/subAcounts/subAccounts';
import { SynchronousIntegration } from './subSteps/synchronousIntegration/synchronousIntegration';
import { Transfer } from './subSteps/transfer/transfer';
import { TypeSelect } from './subSteps/typeSelect/typeSelect';
import { UserVerification } from './subSteps/userVerification/userVerification';
import { WithdrawForm } from './subSteps/withdrawForm/withdrawForm';
import { WithdrawReview } from './subSteps/withdrawReview/withdrawReview';
import WorkflowReview from './subSteps/workflowReview/workflowReview';
import { WorkflowCompletionContext } from './workflowCompletionDialogContext';

export const WorkflowCompletionVisual = ({
  workflowCompletion,
  open,
  onClose,
  onNext,
  previousStep,
  loading,
  data,
  onChangeSetData,
  setActiveWorkflowCompletion,
  isInnerWorkflow,
  handleIdSkip,
  onCloseIconClick = onClose,
  filterReviewStepId,
}: {
  workflowCompletion: any;
  open: boolean;
  onClose: () => void;
  onCloseIconClick?: () => any;
  onNext: (branchId?: string) => void;
  previousStep: () => void;
  loading: boolean;
  data?: any;
  onChangeSetData?: Dispatch<SetStateAction<any>>;
  setActiveWorkflowCompletion?: any;
  isInnerWorkflow?: boolean;
  handleIdSkip?: () => void;
  filterReviewStepId?: string;
}) => {
  const { userContext } = useContext(UserContext);
  const cancelWorkflowEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.CancelWorkflow,
  );
  const [hasQuebecAddress, setHasQuebecAddress] = useState(false);
  const [openSkipIDVerification, setOpenSkipIDVerification] = useState(false);
  const ref = useRef<HTMLInputElement | null>(null);
  const { sendAnalytic } = useContext(AnalyticsContext);

  const { t } = useTranslation('workflowCompletions');
  const theme = useTheme();
  const handleSkip = async () => {
    if (
      hasQuebecAddress &&
      workflowCompletion?.currentSubStep?.skippable !== true
    ) {
      setOpenSkipIDVerification(true);
    } else {
      await onNext();
    }
  };
  if (
    filterReviewStepId &&
    workflowCompletion?.currentSubStep?.id === filterReviewStepId
  ) {
    onClose();
  }
  const handleGoBack = () => {
    if (hasQuebecAddress) setHasQuebecAddress(false);
    sendAnalytic(ovAnalyticsEvents.modelPageCreateBack, {
      stepName: 'CREATE_MODEL_PORTFOLIO',
      workflowCompletionId: workflowCompletion?.id,
      timestamp: new Date().toISOString(),
    });
    previousStep();
  };
  // A step reads best in a single narrow column, so anything wider has to earn
  // it: a step holding two models side by side needs room for two of those
  // columns and everything that sits beside them.
  const subStepMaxWidth = (stepType: string) =>
    ({
      WITHDRAW_FORM: '1000px',
      CALCULATOR: '1000px',
      // Allocation bands are a table of four columns per row, which the width
      // a form is read at cannot hold without cutting the names short.
      EDIT_HOUSEHOLD_MODEL_PORTFOLIO_IPS: '800px',
      REVIEW_IPS_SUMMARY: '1600px',
      // Holdings read against the ones they replace: two columns of weights
      // beside a security name long enough not to be cut short.
      REVIEW_ALLOCATIONS: '900px',
      // Editing holdings puts a chart beside a table of five columns, which
      // only has the room to sit that way well past a form's width. The name
      // the create-model-portfolio workflow sends is left at the width that
      // flow has always read at.
      ADD_MODEL_PORTFOLIO_SECURITY_ALLOCATIONS: '1200px',
    })[stepType] ?? '552px';
  const subStepMapping = (subStep: any) => {
    switch (subStep?.type) {
      case 'ACCOUNTS':
        return (
          <Account
            options={subStep.options}
            onNext={onNext}
            stepLoading={loading}
            userId={workflowCompletion.objectId}
          />
        );

      case 'APPROVAL':
        return (
          <Approval
            objectId={workflowCompletion.objectId}
            objectType={workflowCompletion.objectType}
            options={subStep.options}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'DOCUMENTS':
        return (
          <Documents
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            objectId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
            objectType={workflowCompletion.objectType}
          />
        );

      case 'EMPLOYMENT_INFORMATION':
        return (
          <EmploymentInformation
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'GOALS':
        return (
          <Goals
            options={subStep.options}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'HOUSEHOLD':
        return (
          <Household
            options={subStep.options}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'INITIALIZE_HOUSEHOLD_MODEL_PORTFOLIO':
        return (
          <InitializeHouseholdModelPortfolio
            options={subStep.options}
            clientGroupId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'ID_VERIFICATION':
        return (
          <IdVerification
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'INCOME_ASSETS_AND_DEBTS':
        return (
          <IncomeAssetsAndDebts
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'INVESTMENT_KNOWLEDGE':
        return (
          <InvestmentKnowledge
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'CREATE_ASSET_TEMPLATE':
        return (
          <CreateAssetTemplate
            options={subStep.options}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'CREATE_MODEL_PORTFOLIO':
        return (
          <CreateModelPortfolio
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            onNext={onNext}
            stepLoading={loading}
            onBack={previousStep}
          />
        );

      case 'ADD_MODEL_PORTFOLIO_DETAILS':
        return (
          <AddModelPortfolioDetails
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      // The first name is the one the hardcoded create-model-portfolio
      // workflow sends; the second is what workflow-service calls the same
      // step, and is the only one an admin can put in a workflow.
      case 'ADD_MODEL_PORTFOLIO_SECURITIES':
      case 'ADD_MODEL_PORTFOLIO_SECURITY_ALLOCATIONS':
        return (
          <AddModelPortfolioSecurities
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'ADD_IPS_AND_ASSET_CLASSES_DISTRIBUTION':
        return (
          <AddModelPortfoliosIPSAndAssetClasses
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'PERSONAL_INFORMATION':
        return (
          <PersonalInformation
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'USER_VERIFICATION':
        return (
          <UserVerification
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'PERSONS_OF_INTEREST':
        return (
          <PersonsOfInterest
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'RELATED_ENTITIES':
        return (
          <RelatedEntities
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'RELATIONSHIP_INFORMATION':
        return (
          <RelationshipInformation
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'RESIDENCY_INFORMATION':
        return (
          <ResidencyInformation
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'RISK_QUESTION_1':
        return (
          <RiskQuestion
            question={1}
            workflowCompletion={workflowCompletion}
            options={subStep.options}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'RISK_QUESTION_2':
        return (
          <RiskQuestion
            question={2}
            workflowCompletion={workflowCompletion}
            options={subStep.options}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'SUB_ACCOUNTS':
        return (
          <SubAccounts
            options={subStep.options}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'CREATE_GOAL':
        return (
          <CreateGoal
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            objectId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
            objectType={workflowCompletion.objectType}
          />
        );

      case 'EDIT_GOAL':
        return (
          <EditGoal
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            objectId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
            objectType={workflowCompletion.objectType}
          />
        );

      case 'REVIEW_GOAL':
        return (
          <ReviewGoal
            options={subStep.options}
            onNext={onNext}
            userId={workflowCompletion.objectId}
            setActiveWorkflowCompletion={setActiveWorkflowCompletion}
            workflowCompletion={workflowCompletion}
          />
        );

      case 'WORKFLOW_REVIEW':
        return (
          <WorkflowReview
            options={subStep.options}
            onNext={onNext}
            workflowCompletion={workflowCompletion}
            userId={workflowCompletion.objectId}
          />
        );

      case 'GOAL_RISK_QUESTION_1':
        return (
          <GoalRiskQuestion
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            objectId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
            objectType={workflowCompletion.objectType}
          />
        );

      case 'CREATE_ACCOUNT_FOR_GOAL':
        return (
          <CreateAccountForGoal
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            objectId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
            objectType={workflowCompletion.objectType}
          />
        );

      case 'EDIT_ACCOUNT':
        return (
          <EditAccount
            direction={workflowCompletion?.direction ?? 'FORWARD'}
            options={subStep.options}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
            previousStep={previousStep}
            workflowCompletion={workflowCompletion}
          />
        );

      case 'REVIEW_ACCOUNT':
        return (
          <ReviewAccount
            options={subStep.options}
            onNext={onNext}
            setActiveWorkflowCompletion={setActiveWorkflowCompletion}
            workflowCompletion={workflowCompletion}
            userId={workflowCompletion.objectId}
          />
        );

      case 'CREATE_SUBACCOUNT_FOR_ACCOUNT':
        return (
          <CreateSubAccountForAccount
            options={subStep.options}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'ASSIGN_PORTFOLIO_TO_SUB_ACCOUNT':
        return (
          <AssignPortfolioToSubAccount
            options={subStep.options}
            objectId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
            workflowCompletion={workflowCompletion}
            objectType={workflowCompletion.objectType}
          />
        );

      case 'REVIEW_PORTFOLIO_CHANGES':
        return (
          <ReviewPortfolioChanges
            options={subStep.options}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'TRANSFERS':
        return (
          <Transfer
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            onNext={onNext}
            stepLoading={loading}
            userId={workflowCompletion.objectId}
          />
        );

      case 'DEPOSIT_TYPE':
        return (
          <DepositType
            workflowCompletion={workflowCompletion}
            options={subStep.options}
            onNext={onNext}
            direction={workflowCompletion?.direction ?? 'FORWARD'}
            stepLoading={loading}
          />
        );

      case 'DEPOSIT_FORM':
        return (
          <DepositForm
            workflowCompletion={workflowCompletion}
            options={subStep.options}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'DEPOSIT_REVIEW':
        return (
          <DepositReview
            options={subStep.options}
            onNext={onNext}
            workflowCompletion={workflowCompletion}
            setActiveWorkflowCompletion={setActiveWorkflowCompletion}
          />
        );

      case 'CALCULATOR':
        return (
          <Calculator
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            subStepId={subStep.id}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'WITHDRAW_FORM':
        return <WithdrawForm options={subStep.options} onNext={onNext} />;
      case 'WITHDRAW_REVIEW':
        return <WithdrawReview options={subStep.options} onNext={onNext} />;
      case 'SIMPLE_FORM':
        return (
          data &&
          onChangeSetData && (
            <SimpleForm
              options={subStep.options}
              onNext={onNext}
              onChangeSetData={onChangeSetData}
              data={data}
              stepLoading={loading}
            />
          )
        );

      case 'TYPE_SELECT':
        return (
          data &&
          onChangeSetData && (
            <TypeSelect
              options={subStep.options}
              onNext={onNext}
              onChangeSetData={onChangeSetData}
              data={data}
            />
          )
        );

      case 'NON_INDIVIDUAL_INFORMATION':
        return (
          <NonIndividualInformation
            workflowCompletion={workflowCompletion}
            options={subStep.options}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'NON_INDIVIDUAL_SETUP':
        return (
          <NonIndividualSetup
            workflowCompletion={workflowCompletion}
            options={subStep.options}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
            workflowCompletionId={workflowCompletion.id}
          />
        );

      case 'PROJECTIONS':
        return (
          <FinancialProjection
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'FINANCIAL_PROJECTIONS':
        return (
          <FinancialProjection
            options={subStep.options}
            workflowCompletion={workflowCompletion}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'REVIEW':
        return (
          <Review
            options={subStep.options}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'REVIEW_BANK_ACCOUNT_INFORMATION':
        return (
          <ReviewBankAccountInformation
            options={subStep.options}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'RISK_PROFILE':
        return (
          <RiskProfile
            options={subStep.options}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'COLLECT_SIGNERS':
        return (
          <CollectSigners
            options={subStep.options}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'CREATE_ACCOUNT':
        return (
          <CreateAccount
            options={subStep.options}
            userId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
            workflowCompletion={workflowCompletion.id}
          />
        );

      case 'PREPARE_FORM_COMPLETIONS':
        return (
          <PrepareFormCompletions
            workflowCompletion={workflowCompletion}
            onNext={onNext}
            onBack={previousStep}
            direction={workflowCompletion?.direction ?? 'FORWARD'}
          />
        );

      case 'MANAGE_SIGNERS':
        return (
          <ManageSigners
            workflowCompletion={workflowCompletion}
            onNext={onNext}
            options={subStep.options}
          />
        );

      case 'CREATE_INTEGRATION_FORM_PACK':
        return (
          <CreateIntegrationFormPack
            workflowCompletion={workflowCompletion}
            onNext={onNext}
            onBack={previousStep}
            direction={workflowCompletion?.direction ?? 'FORWARD'}
          />
        );

      case 'SEND_INTEGRATION_FORM_PACK':
        return (
          <SendIntegrationFormPack
            workflowCompletion={workflowCompletion}
            onNext={onNext}
            onBack={previousStep}
            direction={workflowCompletion?.direction ?? 'FORWARD'}
          />
        );

      case 'SIGN_FORM_COMPLETIONS':
        return (
          <SignFormCompletions
            workflowCompletion={workflowCompletion}
            onNext={onNext}
            options={subStep.options}
          />
        );

      case 'MANAGE_FORM_COMPLETIONS':
        return (
          <ManageFormCompletions
            workflowCompletion={workflowCompletion}
            onNext={onNext}
            options={subStep.options}
            setActiveWorkflowCompletion={setActiveWorkflowCompletion}
          />
        );

      case 'SYNCHRONOUS_INTEGRATION':
        return (
          <SynchronousIntegration
            workflowCompletion={workflowCompletion}
            subStepId={subStep.id}
            onNext={onNext}
            onBack={previousStep}
            options={subStep.options}
            direction={workflowCompletion?.direction ?? 'FORWARD'}
          />
        );

      case 'CREATE_ACCOUNT_GROUP':
        return (
          <CreateAccountGroup
            options={subStep.options}
            objectId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
            objectType={workflowCompletion.objectType}
          />
        );

      case 'NET_WORTH':
        return (
          <NetWorthAdd
            options={subStep.options}
            onNext={onNext}
            workflowCompletion={workflowCompletion}
            subStepId={subStep.id}
          />
        );

      case 'ARCHIVE_OBJECTS':
        return (
          <ArchiveObjects
            options={subStep.options}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'REDIRECT_HANDLER':
        return (
          <RedirectHandler
            workflowCompletion={workflowCompletion}
            onNext={onNext}
            options={subStep.options}
          />
        );

      case 'SELECT_HOUSEHOLD_PARENT_MODEL_PORTFOLIO':
        return (
          <SelectHouseholdParentModelPortfolio
            options={subStep.options}
            clientGroupId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'SELECT_MODEL_PORTFOLIO_EDIT_TYPE':
        return (
          <SelectModelPortfolioEditType
            options={subStep.options}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'EDIT_HOUSEHOLD_MODEL_PORTFOLIO_IPS':
        return (
          <EditHouseholdModelPortfolioIps
            options={subStep.options}
            clientGroupId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'REVIEW_IPS_SUMMARY':
        return (
          <ReviewIpsSummary
            options={subStep.options}
            clientGroupId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'REVIEW_ALLOCATIONS':
        return (
          <ReviewAllocations
            options={subStep.options}
            clientGroupId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      case 'DETACH_MODEL_PORTFOLIO':
        return (
          <DetachModelPortfolio
            options={subStep.options}
            clientGroupId={workflowCompletion.objectId}
            onNext={onNext}
            stepLoading={loading}
          />
        );

      default:
        return (
          <>
            <Typography variant='displayLarge' sx={{ mt: 1 }}>
              {translateBackend(
                workflowCompletion?.currentSubStep?.options?.title,
              )}
            </Typography>
            <Typography variant='bodyLarge' sx={{ mb: 3 }}>
              {translateBackend(
                workflowCompletion?.currentSubStep?.options?.subtitle,
              )}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'end',
              }}
            >
              <Button
                disabled={loading}
                sx={{ mt: 3, textAlign: 'center' }}
                onClick={() => onNext()}
              >
                {t('workflowCompletions:continue')}
              </Button>
            </Box>
          </>
        );
    }
  };

  const subStepPermittedRoles =
    workflowCompletion?.currentSubStep?.rolesCompleteableBy ?? [];
  const userRoleProfileId = userContext?.role?.publicRoleProfile?.id;
  const isSubStepRolePermitted =
    subStepPermittedRoles.length === 0 ||
    subStepPermittedRoles
      .map((role: { id: string }) => role?.id)
      .includes(userRoleProfileId);

  const shouldDisplayCloseButton = cancelWorkflowEnabled
    ? workflowCompletion?.workflow?.settings?.displayCloseButton === undefined
      ? true
      : workflowCompletion?.workflow?.settings?.displayCloseButton
    : true;

  return (
    <WorkflowCompletionContext.Provider
      value={{
        scrollWorkflowDialog: () => {
          if (ref && ref.current) ref?.current?.scrollTo(0, 0);
        },
        setHasQuebecAddress: (value: boolean) => {
          if (workflowCompletion?.currentSubStep?.skippable !== true) {
            setHasQuebecAddress(value);
          }
        },
      }}
    >
      <FullScreenDialog
        isInnerWorkflow={isInnerWorkflow}
        data-testid='workflow-completion-full-screen-dialogue'
        open={open}
      >
        <Box
          sx={{
            display: 'flex',
            height: '100%',
            backgroundColor: legacyColors.white,
          }}
        >
          <Box
            ref={ref}
            sx={{
              flex: 3,
              overflow: 'auto',
              pb: 12,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    p: 3,
                  }}
                >
                  {workflowCompletion?.currentSubStep?.canGoBack ? (
                    <Button
                      variant='text'
                      startIcon={<ChevronLeft />}
                      onClick={() => handleGoBack()}
                    >
                      {t('shared:back')}
                    </Button>
                  ) : (
                    <Box />
                  )}
                  {shouldDisplayCloseButton && (
                    <IconButton
                      aria-label='close'
                      onClick={onCloseIconClick}
                      data-testid='workflow-completion-close'
                    >
                      <CloseIcon fontSize='inherit' />
                    </IconButton>
                  )}
                </Box>
                <Box
                  sx={{
                    pt: '20px',
                    pl: isInnerWorkflow ? 6 : 2,
                    pr: isInnerWorkflow ? 5 : 2,
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  {isSubStepRolePermitted ? (
                    <Box
                      key={workflowCompletion?.currentSubStep?.id}
                      sx={{
                        maxWidth: subStepMaxWidth(
                          workflowCompletion?.currentSubStep?.type,
                        ),

                        width: '100%',
                        pb: 8,
                      }}
                    >
                      <StepIndicator workflowCompletion={workflowCompletion} />
                      {subStepMapping(workflowCompletion?.currentSubStep)}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: 'column',
                        gap: 3,
                      }}
                    >
                      <img src={MugImage} alt='mug-icon' />
                      <Typography
                        variant='displayLarge'
                        sx={{ mt: 1, textAlign: 'center' }}
                      >
                        {t('caughtUp')}
                      </Typography>
                      <Typography
                        variant='bodyLarge'
                        sx={{ mt: 1, maxWidth: '500px', textAlign: 'center' }}
                      >
                        {t('caughtUpSubtitle')}
                      </Typography>
                      <Button variant='text' size='large' onClick={onClose}>
                        {t('workflowCompletions:closeWorkflow')}
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
              {(workflowCompletion?.currentSubStep?.skippable ||
                hasQuebecAddress) && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'end',
                    p: 3,
                  }}
                >
                  <Button
                    variant='text'
                    onClick={handleSkip}
                    data-testid={`skip-button-${kebabCase(workflowCompletion?.currentSubStep?.type)}`}
                  >
                    {t('workflowCompletions:skip')}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </FullScreenDialog>
      <Dialog open={openSkipIDVerification}>
        <DialogTitle>
          {t('workflowCompletions:idVerificationSkipDialog.title')}
        </DialogTitle>
        <DialogContent>
          <Typography variant='bodyLarge'>
            {t('workflowCompletions:idVerificationSkipDialog.content1')}
            <Link
              underline='none'
              target='_blank'
              href={
                translateBackend(
                  userContext.organization?.translatedSupportUrl,
                ) || 'https://onevest.zendesk.com/hc/en-us/requests/new'
              }
            >
              {t('workflowCompletions:idVerificationSkipDialog.content2')}
            </Link>
            {t('workflowCompletions:idVerificationSkipDialog.content3')}
          </Typography>
        </DialogContent>
        <DialogFooter>
          <Button
            sx={{
              backgroundColor: `${theme.color.primaryVariant} !important`,
              color: `${theme.color.onPrimaryVariant} !important`,
            }}
            onClick={() => setOpenSkipIDVerification(false)}
          >
            {t('workflowCompletions:idVerificationSkipDialog.cancel')}
          </Button>
          <Button
            sx={{
              backgroundColor: `${theme.color.primary} !important`,
              color: `${theme.color.onPrimary} !important`,
            }}
            onClick={() => {
              if (handleIdSkip) handleIdSkip();
              setOpenSkipIDVerification(false);
              setHasQuebecAddress(false);
              onNext();
            }}
          >
            {t('workflowCompletions:idVerificationSkipDialog.continue')}
          </Button>
        </DialogFooter>
      </Dialog>
    </WorkflowCompletionContext.Provider>
  );
};

export default WorkflowCompletionVisual;
